/**
 * Cookieless analytics: PostHog (behavior) + GA4 (attribution).
 *
 * Both providers run in memory-only mode: no cookies, no cross-session visitor
 * ID, IP anonymized. That keeps us out of cookie-banner territory while still
 * giving funnels, scroll depth, click data and UTM attribution.
 *
 * Never pass PII (email, name, phone, pilot number) into `track()` — the
 * contract test in src/lib/__tests__/analytics.test.ts enforces this.
 */

// Publishable browser tokens. Env wins so a connector can override them later.
const POSTHOG_KEY =
  (import.meta.env["VITE_LOVABLE_CONNECTOR_POSTHOG_API_KEY"] as string | undefined) ||
  "phc_xM6caBWREoMYXHKpHpec4npCQyDHhFVsaMh5EFZVcHdF";

const POSTHOG_REGION =
  (import.meta.env["VITE_LOVABLE_CONNECTOR_POSTHOG_REGION"] as string | undefined) || "us";

const GA_MEASUREMENT_ID =
  (import.meta.env["VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY"] as string | undefined) ||
  "G-8YRDT614NS";

// Managed reverse proxy in front of PostHog: keeps ingest first-party so
// ad/tracker blockers don't drop events. UI host stays the real PostHog app.
// Verified live: a `proxy_smoke_test` event sent to this host arrived in
// PostHog with `{"status":"Ok"}` (2026-09-03).
export const POSTHOG_PROXY_HOST =
  (import.meta.env["VITE_POSTHOG_PROXY_HOST"] as string | undefined) || "https://d.squadflight.com";

export const POSTHOG_UI_HOST =
  POSTHOG_REGION === "eu" ? "https://eu.posthog.com" : "https://us.posthog.com";

export const POSTHOG_HOST =
  POSTHOG_PROXY_HOST ||
  (POSTHOG_REGION === "eu" ? "https://eu.i.posthog.com" : "https://us.i.posthog.com");

const PII_KEYS = ["email", "phone", "first_name", "last_name", "name", "pilot_no", "callsign"];

export type AnalyticsProps = Record<string, string | number | boolean | null | undefined>;

type PostHogClient = {
  capture: (event: string, props?: Record<string, unknown>) => void;
  init: (key: string, config: Record<string, unknown>) => void;
};

let posthogClient: PostHogClient | null = null;
let initialized = false;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

// GA4 requires the arguments object (array-like), NOT a real array.
// Pushing a plain array is silently ignored by gtag.js.
const gtag: (...args: unknown[]) => void = function () {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
};

/**
 * Which CTA sent the visitor to the form. Remembered in memory so every
 * downstream waitlist event can be attributed to the button that drove it.
 */
let ctaLocation = "none";
export function setCtaLocation(location: string) {
  ctaLocation = location;
}
export function getCtaLocation() {
  return ctaLocation;
}

/** Strips PII keys and undefined values before anything leaves the browser. */
export function sanitizeProps(props?: AnalyticsProps): Record<string, unknown> {
  const clean: Record<string, unknown> = {};
  if (!props) return clean;
  for (const [key, value] of Object.entries(props)) {
    const normalized = key.toLowerCase().replace(/[^a-z_]/g, "_");
    if (PII_KEYS.some((pii) => normalized === pii || normalized.endsWith(`_${pii}`))) continue;
    if (value === undefined) continue;
    clean[key] = value;
  }
  return clean;
}

/** UTM + referrer context, read once per pageview. */
export function pageContext(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const ctx: Record<string, unknown> = {
    path: window.location.pathname,
    referrer: document.referrer || "direct",
  };
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
    const value = params.get(key);
    if (value) ctx[key] = value;
  }
  return ctx;
}

export async function initAnalytics() {
  if (typeof window === "undefined" || initialized) return;
  initialized = true;

  if (POSTHOG_KEY) {
    const { default: posthog } = await import("posthog-js");
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: POSTHOG_UI_HOST,
      persistence: "memory",
      autocapture: true,
      capture_pageview: false,
      capture_pageleave: true,
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-ph-mask]",
      },
      person_profiles: "identified_only",
    });
    posthogClient = posthog as unknown as PostHogClient;
  }

  if (GA_MEASUREMENT_ID) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    window.gtag = gtag;

    gtag("js", new Date());
    gtag("config", GA_MEASUREMENT_ID, {
      anonymize_ip: true,
      client_storage: "none",
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      send_page_view: false,
    });
  }
}

/** Fans one event out to PostHog and GA4. No-op when providers aren't configured. */
export function track(event: string, props?: AnalyticsProps) {
  if (typeof window === "undefined") return;
  const clean = sanitizeProps(props);
  posthogClient?.capture(event, clean);
  if (GA_MEASUREMENT_ID) gtag("event", event, clean);
}

/**
 * GA4's recommended lead event, so a waitlist signup can be marked a key event
 * (conversion) in Analytics. GA4 only — PostHog already gets the descriptive
 * `waitlist_signup_succeeded` event, and we don't want it counted twice there.
 */
export function trackLeadConversion(props?: AnalyticsProps, eventId?: string) {
  if (typeof window === "undefined" || !GA_MEASUREMENT_ID) return;
  gtag("event", "generate_lead", {
    ...sanitizeProps(props),
    ...(eventId ? { event_id: eventId } : {}),
  });
}

/**
 * The GA4 client id for this browser, so a server-side copy of the same
 * conversion lands in the same session instead of looking like a new visitor.
 * Resolves to null when GA is unconfigured or its script was blocked.
 */
export function getGaClientId(timeoutMs = 1500): Promise<string | null> {
  if (typeof window === "undefined" || !GA_MEASUREMENT_ID) return Promise.resolve(null);
  return new Promise((resolve) => {
    let settled = false;
    const done = (value: string | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    // Blocked gtag never calls back — don't hold the form hostage.
    window.setTimeout(() => done(null), timeoutMs);
    try {
      gtag("get", GA_MEASUREMENT_ID, "client_id", (id: unknown) =>
        done(typeof id === "string" ? id : null),
      );
    } catch {
      done(null);
    }
  });
}

/** Shared id that ties the browser hit and the server hit to one conversion. */
export function newAnalyticsEventId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `sf-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

export function trackPageview(path: string) {
  if (typeof window === "undefined") return;
  const clean = { ...pageContext(), path };
  posthogClient?.capture("$pageview", clean);
  if (GA_MEASUREMENT_ID) gtag("event", "page_view", { page_path: path, ...clean });
}
